import { useState, useContext, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Divider, Grid, Paper, Stack, SvgIcon, Typography } from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import HotWaterChart from './hotWaterChart'
import RightPanel from '../rightPanel'
import PauseBanner from './pauseBanner';
import PromptBanner from './promptBanner';
import StatusMenu from './statusMenu';
import AiChatHelp from '../../../components/right-panel/help/aiChat'
import { AuthContext } from '../../../contexts/auth/authContext'
import { DataContext, DataDispatchContext } from '../../../contexts/data/dataContext'
import { getPromptAnswer } from '../../../services/api-query/customer'
import { 
  chatIcon
} from '../../../components/common/iconPaths'
import Notification from '../../common/notification';
import custom from '../../../styles/mui/szVariables.json'
import { analyticsPage, analyticsDomain } from '../../../services/constants'
import { clickAnalyticsHW, sendViewAnalytics } from '../../common/util/util'

export default function HotWater() {
  const border = custom.border
  const navigate = useNavigate()
  const auth = useContext(AuthContext)
  const { dashboard, rightPanel, chat, askAIhelp, setSavingMode } = useContext(DataContext)
  const hotwater = dashboard?.hotwater
  const dispatch: any = useContext(DataDispatchContext)
  const [error, setError] = useState('')
  const [energySavingModeStatus, setEnergySavingModeStatus] = useState('Enabled')
  const [status, setStatus] = useState('Heating')
  const [powerIconColor, setPowerIconColor] = useState('Heating')
  const [thermometerColor, setThermometerColor] = useState('OnBlue')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [faqShow, setFaqShow] = useState(false);
  const [staffError, setStaffError] = useState('')
  
  function handleFaqShow() {
    if(!faqShow) {
      sendViewAnalytics('/rightPanel', {
            domain: analyticsDomain.hotWater,
            name: analyticsPage.hotWaterTroubleshooting
          })
    }
    setFaqShow(!faqShow)
    clickAnalyticsHW('troubleshooting')
  }

  useEffect(() => {
    function statusLogic() {
      if (hotwater?.circuitOn && hotwater?.heatingOn) {
        setStatus('Heating')
        setPowerIconColor('Heating')
        setThermometerColor('OnBlue')
      } else if (hotwater?.circuitOn && !hotwater?.heatingOn) {
        setStatus('Standby')
        setPowerIconColor('Standby')
        setThermometerColor('OnGrey')
      } else if (!hotwater?.circuitOn) {
        setStatus('Not heating as the energy price is higher')
        setPowerIconColor('NotHeating')
        setThermometerColor('Off')
      }
      setEnergySavingModeStatus(hotwater?.energySavingMode?.status)
    }
    statusLogic()
  }, [hotwater])

  useEffect(()=>{
    sendViewAnalytics('/rightPanel', {
            domain: analyticsDomain.hotWater,
            name: analyticsPage.hotWaterMain
          })
  },[])
  

  function energySavingModeSwitch(status: string) {
    setAnchorEl(null)
    clickAnalyticsHW(status)
    setError('')
    setSavingMode(status)
    .then((res: string)=>{
      if(res!=='saved') {
        setError(res)
      } else {
        dispatch({
          type: 'setEnergySavingMode',
          status
        })
        setEnergySavingModeStatus(status)
      }
    })
  }

  function handleStatusMenu(event: any) {
    setError('')
    setStaffError('')
    if(auth?.staff) {
      setStaffError("You cannot perform this action\n while impersonating")
    } else {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    }
  }

  const gotoFeedback = ()=>{
    dispatch({
      type: 'setAcctSelected',
      acctSelected: 'help'
    })
    navigate('/profile')
  }

  const askAssistant = (id: number)=>{
    const faqId = hotwater?.troubleshooting?.faqs[id].id
    clickAnalyticsHW('question', { faqId })
    const question = hotwater?.troubleshooting?.faqs[id].question
    if(rightPanel?.key!=='help') {
      dispatch({
        type: 'setRightPanel',
        rightPanel: <AiChatHelp key="help"/>
      })
    }
    dispatch({
      type: 'newAiConversation',
      message: question,
      from: 'user',
      chatWindow: chat.chatWindow + 5,
      key: uuidv4()
    })
    askAIhelp(question)
  }

  return (
    <RightPanel title="Hot water">
      <Grid
        id="hotwaterContent"
        container
        direction="column"
        display={'flex'}
        justifyContent="flex-start"
        pb={10}>
        <Grid item xs width="100%">
          <Box height={20}>
            <Alert severity='error' 
                sx={{ display: error?'block':'none' }}>
                {error}
            </Alert>
            {energySavingModeStatus === 'Paused' && (
              <PauseBanner 
                message={hotwater?.energySavingMode?.pausedDescription} />
            )}
            {energySavingModeStatus !== 'Paused' 
              && !getPromptAnswer() && (
                <PromptBanner />
              )}
            </Box>
        </Grid>  
        <Grid item xs width="100%" display="flex" 
              justifyContent={'center'} 
              mt={7}>
          <Box
            component={'img'}
            height="auto"
            width="50%"
            loading="lazy"
            alt="Hot Water"
            src={`/images/Heating${thermometerColor}.svg`}
          />
        </Grid>
        <Grid item xs width="100%" display="flex" 
              justifyContent={'center'} 
              mt={custom.spacing.sm}>
            <Stack direction="row" 
                  spacing={custom.spacing.xxs}>
              {status === 'Heating' && (
                <>
                  <Box
                    component={'img'}
                    loading="lazy"
                    alt="Bolt Icon"
                    src="/images/BoltIcon.svg"
                    alignItems="center"
                  />
                  <Typography variant="szBody.Bold.Medium"
                      id="hotwaterWattageValue">
                        {hotwater?.wattage}
                  </Typography>
                  <Typography variant="szBody.Regular.Small">
                      kW
                  </Typography>
                </>
              )}
            </Stack>
        </Grid>
        <Grid item xs width="100%" mt={custom.spacing.md}>
          <Stack alignItems="center" >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'sz.colors.helloYellow',
                borderRadius: custom.corners5,
                border,
                boxSizing: 'border-box',
                width: 'auto',
              }}>
              <Box p={custom.spacing.xxs}>
                <Stack direction="row" spacing={custom.spacing.xxs}>
                  <Box
                    component={'img'}
                    loading="lazy"
                    alt="Power Icon"
                    src={`/images/PowerIcon${powerIconColor}.svg`}
                    p={custom.spacing.xxxxs}
                    alignItems="center"
                    sx={{
                      backgroundColor:
                        powerIconColor === 'Heating'
                          ? 'sz.colors.green'
                          : powerIconColor === 'Standby'
                          ? 'sz.colors.gray100'
                          : 'sz.colors.helloYellow',
                      borderRadius: custom.corners4,
                    }}
                  />

                  <Typography variant="szBody.Regular.Small"
                     id="hotwaterStatus">
                      {status}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Grid>
        <Grid item xs width="100%" mt={custom.spacing.md}>
          <Paper elevation={0} sx={{ 
                borderRadius: custom.corners4, 
                border, 
                mx: custom.spacing.sm
              }}>
            <Box width="100%">
              <Stack p={custom.spacing.sm} 
                  direction="row" 
                  alignItems="center" 
                  justifyContent="space-between">
                <Stack direction="row" 
                      spacing={custom.spacing.xxs}>
                  <Box
                    component={'img'}
                    height="auto"
                    width="20px"
                    loading="lazy"
                    alt="Time Icon"
                    src="/images/TimeIcon.svg"
                  />
                  <Typography variant="szBody.Regular.Small" 
                    noWrap 
                    maxWidth={{
                      xs:'200px', 
                      sm:"100px", 
                      md:'200px',
                      lg:'200px'
                    }}>
                    Energy saving mode
                  </Typography>
                </Stack>
                <Paper elevation={0} 
                        sx={{ 
                          borderRadius: custom.corners3, 
                          border, 
                          boxSizing: 'border-box', 
                          p: custom.spacing.xxxs,
                        }}>
                  <Stack
                    id="hotwaterEnergySavingStatus"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={handleStatusMenu}
                    sx={{ cursor:'pointer' }}>
                    <Box
                      component={'img'}
                      height="auto"
                      width="25px"
                      loading="lazy"
                      alt="Hot water status"
                      src={`/images/EnergySaving${
                        energySavingModeStatus === 'Enabled'
                          ? 'Enabled'
                          : energySavingModeStatus === 'Disabled'
                          ? 'Disabled'
                          : 'Paused'
                      }.svg`}
                    />
                    <Typography variant="szBody.Regular.Small" 
                          sx={{ p: custom.spacing.xxxs }} 
                          id="hotwaterEnergySavingStatusValue">
                      {energySavingModeStatus === 'Enabled'
                        ? 'Enabled'
                        : energySavingModeStatus === 'Disabled'
                        ? 'Disabled'
                        : 'Paused'}
                    </Typography>
                      <Box
                        component={'img'}
                        height="auto"
                        width="25px"
                        loading="lazy"
                        alt="Hot water status"
                        src="/images/dropdown.svg"
                      />
                  </Stack>
                </Paper>
              </Stack>
              <Notification 
                  title={"Error"} 
                  message={staffError}
                  type="error"
                  setError={setStaffError}
                  hposition="center"
                  />
              <Divider orientation="horizontal" flexItem />
              <HotWaterChart />
              <Stack p={custom.spacing.sm} 
                  direction="row" 
                  alignItems="center" 
                  justifyContent="space-between">
                <Stack direction="column">
                  <Typography variant="szHeading.Bold.xSmall"
                     id="hotwaterUsedTodayValue">
                    {dashboard?.hotwater?.usedToday?.amount}
                    {dashboard?.hotwater?.usedToday?.unit}
                  </Typography>
                  <Typography variant="szBody.Regular.xxSmall" 
                        sx={{ color: 'text.tertiary' }}>
                    Used today
                  </Typography>
                </Stack>
                <Stack direction="column">
                  <Typography variant="szHeading.Bold.xSmall"
                     id="hotwaterWeeklyAverageValue">
                    {dashboard?.hotwater?.weeklyAverage?.amount}
                    {dashboard?.hotwater?.weeklyAverage?.unit}
                  </Typography>
                  <Typography variant="szBody.Regular.xxSmall" 
                      sx={{ color: 'text.tertiary' }}>
                    7 day average
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs width="100%" display={'flex'} 
              justifyContent={'center'} >
          <Box mx={custom.spacing.sm}>
            <Typography variant="szBody.Regular.xSmall" 
                sx={{ color: 'text.tertiary' }} 
                id="hotwaterComment">
              {dashboard?.hotwater?.comment}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs width="100%" display={'flex'} 
              justifyContent={'center'} >
          <Paper
            elevation={0}
            sx={{
              mx: custom.spacing.sm,
              mt: custom.spacing.sm,
              borderRadius: custom.corners4,
              border,
              boxSizing: 'border-box',
              width: '100%'
            }}>
            <Stack direction="row" 
                  alignItems="center" 
                  justifyContent="space-between" 
                  p={custom.spacing.sm}
                  id="hotwaterTroubleshootingButton"
                  onClick={handleFaqShow}>
              <Stack direction="row" 
                    spacing={custom.spacing.xxs}>
                <Box
                  component={'img'}
                  height="auto"
                  width="20px"
                  loading="lazy"
                  alt="Info Icon"
                  src="/images/InfoIcon.svg"
                />
                <Typography variant="szBody.Regular.Small">
                    Troubleshooting
                </Typography>
              </Stack>
              {!faqShow?
                <Box
                  component={'img'}
                  height="auto"
                  width="20px"
                  loading="lazy"
                  alt="Chevron Icon"
                  src="/images/ChevronDown.svg"
                />
                :
                <Box
                  component={'img'}
                  height="auto"
                  width="20px"
                  loading="lazy"
                  alt="Chevron Icon"
                  src="/images/ChevronUp.svg"
                />
              }
            </Stack>
            <Box display={faqShow?'block':'none'}>
                {(hotwater?.troubleshooting?.faqs || [])
                  .map((faq: any, i: number) => (
                  <Box
                   sx={{
                     borderTop: border,
                     width: '100%',
                     cursor: 'pointer',
                     '&:hover': {
                       backgroundColor: 'sz.colors.hover',
                       boxShadow: 'none',
                     },
                   }}
                   key={i}
                   id={`hotwaterTroubleshootingFaq${i+1}`}
                   onClick={()=>askAssistant(i)}>
                   <Stack direction="row" alignItems="center">
                     <Typography m={custom.spacing.sm} 
                        variant="szBody.Regular.Medium">
                       {faq.question}
                     </Typography>
                     <ArrowForwardIosIcon sx={{ 
                          marginLeft: 'auto', 
                          pt: custom.spacing.xxxs, 
                          mr: custom.spacing.xxs, 
                          height: '1rem' 
                      }} />
                   </Stack>
                  </Box>
                ))}
                <Box
                   sx={{
                     borderTop: border,
                     width: '100%',
                     cursor: 'pointer',
                     '&:hover': {
                       backgroundColor: 'sz.colors.hover',
                       boxShadow: 'none',
                     },
                   }}
                   id="sendFeedback"
                   onClick={gotoFeedback}>
                   <Stack direction="row" alignItems="center">
                      <SvgIcon sx={{ 
                        ml: custom.spacing.sm, 
                        color: 'primary.main'}}>
                          {chatIcon}
                      </SvgIcon>
                     <Typography m={custom.spacing.sm} 
                        variant="szBody.Regular.Medium">
                       Send feedback
                        <Typography component="p" 
                            variant="szBody.Regular.xSmall">
                            Tell us what you think of this feature.
                        </Typography>
                     </Typography>
                     <ArrowForwardIosIcon sx={{
                          ml: 'auto', 
                          pt: custom.spacing.xxxs, 
                          mr: custom.spacing.xxs, 
                          height: '1rem' 
                        }} />
                   </Stack>
                </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <StatusMenu anchorEl={anchorEl} 
        setStatus={energySavingModeSwitch} 
        close={setAnchorEl}/>
    </RightPanel>
  )
}
